import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Image, Row } from 'react-bootstrap';
import { PlatformItem } from '../../../model/platform/PlatformItem';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { PlatformService } from '../PlatformService';
import { ErrorToast, SuccessToast, WarningToast } from '../../../utils/toasters';
import { Platform } from '../../../model/platform/Platform';
import { initialPlatform } from '../dto/initialPlatform';
import { multipartFormData } from '../../../api/api.service';
import { PLATFORM_ITEM_URL } from '../../../utils/Endpoints';
import { useNavigate } from 'react-router-dom';
import CardLayout from '../../../shared/components/CardLayout';
import SectionTitle from '../../../shared/components/SectionTitle';
import ToggleChip from '../../../shared/components/ToggleChip';
import { getCorrectEnums } from '../../../utils/Functions';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';

const STATUS_FIELDS: { label: string; key: keyof PlatformItem }[] = [
    { label: 'Advertising', key: 'hasAdvertising' },
    { label: 'Long Term', key: 'isLongTerm' },
    { label: 'Live', key: 'isLive' },
    { label: 'Enabled', key: 'isEnabled' },
];

const METRIC_FIELDS: { label: string; key: keyof PlatformItem }[] = [
    { label: 'Likes', key: 'likes' },
    { label: 'Dislikes', key: 'dislikes' },
    { label: 'Comments', key: 'comments' },
    { label: 'Shares', key: 'shares' },
    { label: 'Saves', key: 'saves' },
    { label: 'Link Clicks', key: 'swipeUpLinks' },
    { label: 'Views', key: 'views' },
    { label: 'Clicks', key: 'clicks' },
    { label: 'Reach', key: 'reach' },
    { label: 'Watch Time', key: 'watchTime' },
    { label: 'Sticker Engagements', key: 'stickerEngagements' },
    { label: 'Impressions', key: 'impressions' },
    { label: 'Completed Views', key: 'completedViews' },
    { label: 'Video Length', key: 'videoLength' },
    { label: 'Reposts', key: 'reposts' },
];

export function PlatformItemForm() {
    const { platformItemId } = useParams<{ platformItemId: string }>();
    const [mainPlatforms, setMainPlatforms] = useState<Platform[]>([]);
    const [selectedFileLight, setSelectedFileLight] = useState(null);
    const [selectedFileDark, setSelectedFileDark] = useState(null);
    const [imagePath, setImage] = useState({
        lightTheme: '',
        darkTheme: '',
    });
    const [platform, setPlatform] = useState<Partial<PlatformItem>>({
        comments: false,
        dislikes: false,
        hasAdvertising: false,
        icon: '',
        isEnabled: false,
        isLive: false,
        isLongTerm: false,
        likes: false,
        socialPlatform: initialPlatform,
        mainPlatform: null,
        name: '',
        platform: '',
        clicks: false,
        saves: false,
        reach: false,
        stickerEngagements: false,
        swipeUpLinks: false,
        views: false,
        watchTime: false,
        position: 0,
        shares: false,
        type: '',
        path: '',
        title: '',
        impressions: undefined,
        mobileIdentify: '',
        completedViews: false,
        videoLength: false,
        reposts: false,
    });
    const navigate = useNavigate();

    const { platformType } = useSelector((state: any) => state.enums);

    useEffect(() => {
        if (platformItemId) {
            PlatformService.getPlatformItem(+platformItemId)
                .then(response => {
                    setPlatform(response);
                    if (response.path) {
                        setImage({
                            lightTheme: response.path,
                            darkTheme: response.darkPath ?? '',
                        });
                    }
                })
                .catch(err => ErrorToast(err));
        }
    }, []);

    useEffect(() => {
        if (platformItemId) return;
        PlatformService.getPlatformItems({ name: '', page: 1, perPage: 1000 })
            .then(response => {
                const maxPosition = Math.max(0, ...response.data.map(item => item.position ?? 0));
                setPlatform(prev => ({ ...prev, position: maxPosition + 1 }));
            })
            .catch(err => ErrorToast(err));
    }, []);

    useEffect(() => {
        PlatformService.getPlatforms()
            .then(response => {
                setMainPlatforms(response.data);
            })
            .catch(err => ErrorToast(err));
    }, []);

    const createPlatform = () => {
        if (platform.position !== null && platform.name !== '' && selectedFileLight !== null) {
            multipartFormData(selectedFileLight, PLATFORM_ITEM_URL, JSON.stringify(platform), 'post')
                .then(response => {
                    if (selectedFileDark !== null && response?.data?.id) {
                        multipartFormData(
                            selectedFileDark,
                            `${PLATFORM_ITEM_URL}/dark/${response?.data?.id}`,
                            JSON.stringify({}),
                            'post'
                        )
                            .then(response => response)
                            .catch(err => ErrorToast(err));
                    }
                    SuccessToast('Successfully created platform item');
                    navigate('/platform/item');
                })
                .catch(err => ErrorToast(err));
        } else {
            WarningToast('Please fill out all mandatory fields');
        }
    };

    const updatePlatform = () => {
        if (!!selectedFileDark) {
            multipartFormData(
                selectedFileDark,
                `${PLATFORM_ITEM_URL}/dark/${platformItemId}`,
                JSON.stringify({}),
                'post'
            )
                .then(response => response)
                .catch(err => ErrorToast(err));
        }
        multipartFormData(selectedFileLight, `${PLATFORM_ITEM_URL}/${platformItemId}`, JSON.stringify(platform))
            .then(() => {
                SuccessToast('Successfully updated platform item');
                navigate('/platform/item');
            })
            .catch(err => ErrorToast(err));
    };
    function handleChange(event: any, isMainPlatforms = false, isNumber = false) {
        const rawValue = event.target.value;
        const value = isNumber ? +rawValue : rawValue;

        if (isMainPlatforms) {
            const selectedPlatform = rawValue ? mainPlatforms.find(p => p.id === +rawValue) : null;

            setPlatform({
                ...platform,
                mainPlatform: selectedPlatform,
            });
        } else {
            setPlatform({
                ...platform,
                [event.target.name]: value,
            });
        }
    }

    function handleCheckbox(evt: any) {
        const change = evt.target.checked;
        setPlatform({
            ...platform,
            [evt.target.name]: change,
        });
    }

    function handleFileInput(evt: any, isLight = false) {
        if (evt.target.files.length > 0) {
            if (isLight) {
                setSelectedFileLight(evt.target.files[0]);
            } else {
                setSelectedFileDark(evt.target.files[0]);
            }
            setImage(prev => ({
                ...prev,
                [isLight ? 'lightTheme' : 'darkTheme']: URL.createObjectURL(evt.target.files[0]),
            }));
        }
    }

    return (
        <CardLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h4 className="fw-bold mb-0">{platformItemId ? 'Update platform item' : 'Create platform item'}</h4>
                <Button
                    onClick={platformItemId ? updatePlatform : createPlatform}
                    variant="primary"
                    size="sm"
                    className="px-4"
                >
                    Save
                </Button>
            </div>
            <hr className="mb-4" />

            <Form>
                <Row>
                    <Col md={8} className="pr-8">
                        <SectionTitle title="General" />
                        <Row className="mb-2">
                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label className={platformItemId ? 'fw-semibold' : 'required fw-semibold'}>
                                    Platform
                                </Form.Label>
                                <Form.Control name="platform" onChange={handleChange} value={platform?.platform} />
                            </Form.Group>

                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label className={platformItemId ? 'fw-semibold' : 'required fw-semibold'}>
                                    Name
                                </Form.Label>
                                <Form.Control name="name" onChange={handleChange} value={platform?.name} />
                            </Form.Group>

                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label className={platformItemId ? 'fw-semibold' : 'required fw-semibold'}>
                                    Type
                                </Form.Label>
                                <SingleSelectDropdown
                                    placeholder="Select type"
                                    options={(platformType ?? []).map((type: string) => ({
                                        value: type,
                                        label: getCorrectEnums(type),
                                    }))}
                                    selected={toSelectValue(platform?.type)}
                                    onChange={value => handleChange({ target: { name: 'type', value } })}
                                />
                            </Form.Group>

                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label className={platformItemId ? 'fw-semibold' : 'required fw-semibold'}>
                                    Main platforms
                                </Form.Label>
                                <SingleSelectDropdown
                                    placeholder="Select main platform"
                                    options={mainPlatforms.map(p => ({
                                        value: String(p.id),
                                        label: p.name,
                                        image: p.path,
                                    }))}
                                    withImage
                                    selected={toSelectValue(platform?.mainPlatform?.id)}
                                    onChange={value => handleChange({ target: { value } }, true, false)}
                                />
                            </Form.Group>

                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label className={platformItemId ? 'fw-semibold' : 'required fw-semibold'}>
                                    Position
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    name="position"
                                    onChange={event => handleChange(event, false, true)}
                                    value={platform?.position}
                                />
                            </Form.Group>

                            <Form.Group as={Col} md={6} className="mb-3">
                                <Form.Label className={platformItemId ? 'fw-semibold' : 'required fw-semibold'}>
                                    Mobile Identify
                                </Form.Label>
                                <Form.Control
                                    name="mobileIdentify"
                                    onChange={event => handleChange(event, false)}
                                    value={platform?.mobileIdentify}
                                />
                            </Form.Group>
                        </Row>

                        <SectionTitle title="Status" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                            {STATUS_FIELDS.map(({ label, key }) => (
                                <ToggleChip
                                    key={key}
                                    name={key}
                                    onChange={handleCheckbox}
                                    label={label}
                                    checked={!!platform?.[key]}
                                />
                            ))}
                        </div>

                        <SectionTitle title="Metrics" />
                        <div className="d-flex flex-wrap gap-3 mb-2">
                            {METRIC_FIELDS.map(({ label, key }) => (
                                <ToggleChip
                                    key={key}
                                    name={key}
                                    onChange={handleCheckbox}
                                    label={label}
                                    checked={!!platform?.[key]}
                                />
                            ))}
                        </div>
                    </Col>
                    <Col md={4}>
                        <SectionTitle title="Light theme" />
                        <Form.Group>
                            <Form.Label className={platformItemId ? '' : 'required'}>Upload photo</Form.Label>
                            <Form.Control
                                accept=".svg, .jpg, .png "
                                type="file"
                                onChange={e => handleFileInput(e, true)}
                            />
                            <div className="d-flex align-items-center justify-content-center border rounded-3 mt-3 p-3 bg-light">
                                <Image
                                    width={140}
                                    height={110}
                                    style={{ objectFit: 'contain' }}
                                    alt={imagePath.lightTheme || 'Light theme icon'}
                                    src={imagePath.lightTheme || '/images/placeholder.png'}
                                />
                            </div>
                        </Form.Group>

                        <SectionTitle title="Dark theme" />
                        <Form.Group>
                            <Form.Label>Upload photo</Form.Label>
                            <Form.Control
                                accept=".svg, .jpg, .png "
                                type="file"
                                onChange={e => handleFileInput(e, false)}
                            />
                            <div className="d-flex align-items-center justify-content-center border rounded-3 mt-3 p-3 bg-dark">
                                <Image
                                    width={140}
                                    height={110}
                                    style={{ objectFit: 'contain' }}
                                    alt={imagePath.darkTheme || 'Dark theme icon'}
                                    src={imagePath.darkTheme || '/images/placeholder.png'}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
        </CardLayout>
    );
}
